import { useState, useEffect, useRef, useId } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { INIT_DELAY_MS, LOAD_ERROR_MSG } from '../constants';

// Load Stripe once at module level (reused across renders)
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

export function useStripeCard({ enabled }) {
  const [stripeReady, setStripeReady] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const stripeRef = useRef(null);
  const elementsRef = useRef(null);
  const cardElementRef = useRef(null);
  const mountedRef = useRef(false);
  const uniqueId = useId();
  const containerId = `card-container-${uniqueId.replace(/:/g, '')}`;

  useEffect(() => {
    if (!enabled) return;
    if (mountedRef.current) return;

    mountedRef.current = true;
    let cancelled = false;
    setLoadError(null);

    const initializeStripe = async () => {
      if (cancelled) return;

      try {
        if (!stripePromise) {
          throw new Error('Stripe publishable key not configured');
        }

        const stripe = await stripePromise;
        if (cancelled || !stripe) {
          throw new Error('Failed to load Stripe');
        }

        stripeRef.current = stripe;

        // Create Elements instance
        const elements = stripe.elements();
        if (cancelled) return;

        elementsRef.current = elements;

        // Create and mount the Card Element
        const cardElement = elements.create('card', {
          style: {
            base: {
              fontSize: '15px',
              color: '#33261D',
              fontFamily: '"Quicksand", "Trebuchet MS", Arial, sans-serif',
              fontWeight: '500',
              '::placeholder': { color: '#A8A39E' },
              padding: '2px 0',
            },
            invalid: {
              color: '#e53935',
              iconColor: '#e53935',
            },
          },
          hidePostalCode: true,
        });

        if (cancelled) {
          cardElement.destroy();
          return;
        }

        const container = document.getElementById(containerId);
        if (!container || cancelled) {
          cardElement.destroy();
          return;
        }

        cardElement.mount(`#${containerId}`);
        if (cancelled) {
          cardElement.destroy();
          return;
        }

        cardElementRef.current = cardElement;
        setStripeReady(true);
      } catch (err) {
        if (!cancelled) {
          console.error('Stripe init error:', err.message);
          setLoadError(LOAD_ERROR_MSG);
        }
      }
    };

    const timeoutId = setTimeout(initializeStripe, INIT_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      if (cardElementRef.current) {
        try {
          cardElementRef.current.destroy();
        } catch (e) {}
        cardElementRef.current = null;
      }
      elementsRef.current = null;
      mountedRef.current = false;
      setStripeReady(false);
    };
  }, [enabled, containerId]);

  return {
    stripeRef,
    cardElementRef,
    containerId,
    stripeReady,
    loadError,
  };
}
